import { useState, useEffect } from 'react'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { CalendarIcon, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table'
import { logger } from '@magnetar/utils'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { RefreshCw } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useAdplayByBroadcast, type AdPlayRecord } from '@/hooks/useAdplayByBroadcast'
import { useViewMode } from '@/stores/viewMode'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { useAuth } from '@/hooks/useAuth'
import axios from 'axios'
import { toast } from 'sonner'

interface BroadcastDevice {
  broadcast_device_id: number
  name: string
}

interface Props {
  device: BroadcastDevice
}

export default function BroadcastDeviceAdPlayInfo({ device }: Props) {
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined })
  const [currentPage, setCurrentPage] = useState(1)
  const [isExporting, setIsExporting] = useState(false)
  const { register, watch } = useForm<{ search: string }>({ defaultValues: { search: '' } })
  const search = watch('search')
  const searchLower = search.toLowerCase()
  const { token } = useAuth()

  const pageSize = 20 // 每頁顯示 20 筆記錄

  // 从 useViewMode 获取当前的 supplier_id
  const { id } = useViewMode()

  // Fetch ad play data
  const { records, pagination, isLoading, mutate } = useAdplayByBroadcast({
    broadcastDeviceId: device.broadcast_device_id,
    startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    supplierId: id, // 使用 useViewMode 中的 id 作为 supplier_id
    page: currentPage,
    pageSize: pageSize,
  })

  // 當搜尋條件改變時，重置到第一頁
  const filteredRecords = records.filter(
    (r) => !searchLower || r.advertisement.title?.toLowerCase().includes(searchLower),
  )

  // 當搜尋或日期範圍變更時重置到第一頁
  useEffect(() => {
    setCurrentPage(1)
  }, [search, dateRange.from, dateRange.to])

  // 處理分頁變更
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // 匯出 Excel 功能
  const exportToExcel = async () => {
    if (!token) {
      toast.error('請先登入')
      return
    }

    setIsExporting(true)
    try {
      // 構建查詢參數
      const queryParams = new URLSearchParams({
        page: '1',
        pageSize: '10000', // 獲取所有數據
      })

      if (dateRange.from) {
        queryParams.append('startDate', format(dateRange.from, 'yyyy-MM-dd'))
      }
      if (dateRange.to) {
        queryParams.append('endDate', format(dateRange.to, 'yyyy-MM-dd'))
      }
      if (id) {
        queryParams.append('supplierId', id.toString())
      }

      // 獲取所有數據
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/ad-play/records/by-broadcast-device/${device.broadcast_device_id}?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      const allRecords: AdPlayRecord[] = response.data.data.items || []

      // 根據搜尋條件過濾
      const filteredData = allRecords.filter(
        (r) => !searchLower || r.advertisement.title?.toLowerCase().includes(searchLower),
      )

      if (filteredData.length === 0) {
        toast.warning('沒有數據可匯出')
        return
      }

      // 準備 Excel 數據
      const excelData = [
        ['廣告播放記錄'],
        [
          `裝置名稱：${device.name}`,
          dateRange.from || dateRange.to
            ? `日期區間：${dateRange.from ? format(dateRange.from, 'yyyy/MM/dd') : ''} - ${dateRange.to ? format(dateRange.to, 'yyyy/MM/dd') : ''}`
            : '',
        ],
        [],
        ['播放日期', '廣告ID', '廣告名稱', '供應商'],
        ...filteredData.map((item) => {
          // 直接顯示 UTC 時間 (不轉換)
          const date = new Date(item.played_at)
          const formattedDate = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`

          return [
            formattedDate,
            item.ad_id,
            item.advertisement.title || '無標題',
            item.advertisement?.supplier?.supplier_name || '未知供應商',
          ]
        }),
      ]

      // 創建工作簿
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(excelData)

      // 設置列寬
      ws['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 30 }, { wch: 20 }]

      XLSX.utils.book_append_sheet(wb, ws, '播放記錄')

      // 生成並下載 Excel 檔案
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const fileName = `廣告播放記錄_${device.name}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`
      saveAs(data, fileName)

      toast.success(`成功匯出 ${filteredData.length} 筆記錄`)
    } catch (error) {
      logger.error('匯出 Excel 失敗:', error)
      toast.error('匯出失敗，請稍後再試')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="mt-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">廣告播放資訊</CardTitle>
          <CardDescription>查看廣播裝置廣告播放記錄</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[240px] justify-start text-left">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from
                      ? dateRange.to
                        ? `${format(dateRange.from, 'yyyy-MM-dd')} - ${format(
                            dateRange.to,
                            'yyyy-MM-dd',
                          )}`
                        : format(dateRange.from, 'yyyy-MM-dd')
                      : '選擇日期範圍'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="p-0 w-auto max-w-none bg-popover">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    defaultMonth={dateRange.from}
                    initialFocus
                    onSelect={(range) => {
                      if (range?.from) {
                        setDateRange({ from: range.from, to: range.to || range.from })
                      } else {
                        setDateRange({ from: undefined, to: undefined })
                      }
                    }}
                    numberOfMonths={2}
                    locale={zhTW}
                  />
                </PopoverContent>
              </Popover>

              <input
                type="text"
                placeholder="搜尋廣告名稱"
                className="px-3 py-2 border rounded flex-1"
                {...register('search')}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={exportToExcel}
                disabled={isExporting || isLoading}
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? '匯出中...' : '匯出 Excel'}
              </Button>
              <Button variant="outline" size="icon" onClick={() => mutate()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>播放日期</TableHead>
                <TableHead>廣告ID</TableHead>
                <TableHead>廣告名稱</TableHead>
                <TableHead>供應商</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    載入中...
                  </TableCell>
                </TableRow>
              ) : filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    無資料
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((item, idx: number) => {
                  // 直接顯示 UTC 時間 (不轉換)
                  const date = new Date(item.played_at)
                  const formattedDate = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`

                  return (
                    <TableRow key={idx}>
                      <TableCell>{formattedDate}</TableCell>
                      <TableCell>{item.ad_id}</TableCell>
                      <TableCell>{item.advertisement.title}</TableCell>
                      <TableCell>
                        {item.advertisement?.supplier?.supplier_name || '未知供應商'}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>

          {/* 分頁控件 */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  {/* 上一頁 */}
                  <PaginationItem>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="gap-1 pl-2.5"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>上一頁</span>
                    </Button>
                  </PaginationItem>

                  {/* 頁碼 */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }

                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => handlePageChange(pageNum)}
                          isActive={currentPage === pageNum}
                          className="cursor-pointer"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}

                  {/* 省略號 */}
                  {pagination.totalPages > 5 && currentPage < pagination.totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* 下一頁 */}
                  <PaginationItem>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                      className="gap-1 pr-2.5"
                    >
                      <span>下一頁</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>

              {/* 分頁資訊 */}
              <div className="text-sm text-gray-500 text-center mt-2">
                第 {currentPage} 頁，共 {pagination.totalPages} 頁，總計 {pagination.total} 筆記錄
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
